import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useAuth } from "./useAuth";

/**
 * Hook for components that need to check trial restrictions.
 * Returns utilities for guarding write operations in the UI.
 */
export function useTrialGuard() {
  const { trialStatus, isTrialUser, isTrialExpired } = useAuth();

  // Whether the user can perform write operations (non-trial users only)
  const canWrite = !isTrialUser;

  // Show upgrade prompt via toast notification
  const showUpgradePrompt = useCallback(() => {
    toast.info("This feature requires full access. Please upgrade to continue.", {
      duration: 5000,
      action: {
        label: "Schedule Demo",
        onClick: () => {
          // TODO: Configure Calendly URL for TaskEng
          window.open("https://calendly.com/taskeng/demo", "_blank");
        },
      },
    });
  }, []);

  // Format time remaining for display (e.g., "23 hours 45 minutes")
  const formatTimeRemaining = useCallback((): string | null => {
    if (!trialStatus?.timeRemainingMs) return null;

    const totalMinutes = Math.floor(trialStatus.timeRemainingMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days} day${days !== 1 ? "s" : ""} ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`;
    }

    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
    }

    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }, [trialStatus?.timeRemainingMs]);

  // Compact format for banner (e.g., "23h 45m")
  const formatTimeRemainingShort = useCallback((): string | null => {
    if (!trialStatus?.timeRemainingMs) return null;

    const totalMinutes = Math.floor(trialStatus.timeRemainingMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }

    return `${hours}h ${minutes}m`;
  }, [trialStatus?.timeRemainingMs]);

  return useMemo(
    () => ({
      canWrite,
      isTrialUser,
      isTrialExpired,
      trialStatus,
      showUpgradePrompt,
      formatTimeRemaining,
      formatTimeRemainingShort,
      timeRemainingMs: trialStatus?.timeRemainingMs ?? null,
    }),
    [
      canWrite,
      isTrialUser,
      isTrialExpired,
      trialStatus,
      showUpgradePrompt,
      formatTimeRemaining,
      formatTimeRemainingShort,
    ]
  );
}
